'use client';

import { CustomImage as Image } from '..';
import CustomLink from 'components/shared/CustomLink';
import { useRef, useState } from 'react';
import { RingSizes } from 'content/products/ring';
import styles from './ProductCardRing.module.css';
import {
  CurrencySymbol,
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { useCartStore, useShallow, DotSpinner } from '..';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { useTranslations } from 'next-intl';
import { ProductType } from 'components/shared/Cart/types';
import { Currency } from 'utils/Currency';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMedia,
  ShopCardMediaDimBackdrop,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';

export type RingVariantKind = 'air' | 'pro' | 'diesel' | 'rare';

export type RingSizeOption = {
  size: RingSizes;
  shopifyVariantId: string;
  isOpenSize?: boolean;
};

export type RingSizingKitOption = {
  shopifyVariantId: string;
  productType: ProductType;
};

export type RingColorVariant = {
  key: string;
  name: string;
  swatch: string;
  mainImage: string;
  hoverImage?: string;
  price: number;
  sizes: RingSizeOption[];
  tag?: string;
};

export type ProductCardRingProps = {
  className?: string;
  variant: RingVariantKind;
  productType: ProductType;
  title: string;
  description: string;
  fromPrice: number;
  buyHref: string;
  colors: RingColorVariant[];
  sizingKit?: RingSizingKitOption;
  hasDedicatedUpsell: boolean;
  tag?: string;
  originalPrice?: string;
  discountPercentage?: string;
  isFirst?: boolean;
};

type OverlayStep = 'closed' | 'color' | 'size';

export const ProductCardRing = ({
  className,
  productType,
  title,
  description,
  fromPrice,
  buyHref,
  colors,
  sizingKit,
  hasDedicatedUpsell,
  tag,
  originalPrice,
  discountPercentage,
  isFirst = false,
}: ProductCardRingProps) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        currency: s.currency,
        taxString: s.taxString,
        addToCart: s.addToCart,
      })),
    );
    const defaultColorKey = colors[0]?.key ?? null;
    const [step, setStep] = useState<OverlayStep>('closed');
    const [selectedColorKey, setSelectedColorKey] = useState<string | null>(
      defaultColorKey,
    );
    const [selectedSize, setSelectedSize] = useState<RingSizes | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const overlayRef = useRef<HTMLDivElement>(null);
    const formatPriceWithComma = useFormatPrice();
    const t = useTranslations('ring');
    const tCart = useTranslations('cart');

    const selectedColor =
      colors.find((c) => c.key === selectedColorKey) ?? null;
    const heroColor = selectedColor ?? colors[0];

    const hasMixedPrices = colors.some((c) => c.price !== colors[0].price);
    const displayPrice = selectedColor?.price ?? fromPrice;
    const currency = (CartClient.currency ?? Currency.usd) as Currency;

    const closeOverlay = () => {
      setStep('closed');
      setSelectedColorKey(defaultColorKey);
      setSelectedSize(null);
    };

    useClickOutside(
      overlayRef,
      () => setTimeout(closeOverlay, 200),
      step !== 'closed',
    );

    const handleAddToCart = async () => {
      if (!selectedColor || selectedSize == null) return;
      const sizeOpt = selectedColor.sizes.find((s) => s.size === selectedSize);
      if (!sizeOpt) return;

      // Ring stays LAST: the sizingKitBundle policy makes the last non-kit line
      // the bundle parent that cascades on removal.
      const cartLines = [
        ...(sizeOpt.isOpenSize && sizingKit
          ? [
              {
                shopifyVariantId: sizingKit.shopifyVariantId,
                productType: sizingKit.productType,
              },
            ]
          : []),
        { shopifyVariantId: sizeOpt.shopifyVariantId, productType },
      ];

      try {
        setIsLoading(true);
        await CartClient.addToCart(cartLines, hasDedicatedUpsell);
      } finally {
        setIsLoading(false);
        closeOverlay();
      }
    };

    const hasOpenSize =
      !!selectedColor && selectedColor.sizes.some((s) => s.isOpenSize);

    const isOpen = step !== 'closed';

    return (
      <ShopProductCardRoot
        className={[styles.root, className].filter(Boolean).join(' ')}
      >
        <ShopCardMedia onClick={() => closeOverlay()}>
          {heroColor?.mainImage && (
            <Image
              src={heroColor.mainImage}
              alt={heroColor.name}
              height={448}
              width={300}
              sizes="(min-width: 1024px) calc((100% - 48px) / 3), 100vw"
              priority={isFirst}
              style={{ width: '100%', objectFit: 'cover' }}
            />
          )}
        </ShopCardMedia>
        <ShopCardBody>
          <ShopCardSpecSection className={`ringSpec ${styles.ringSpec}`}>
            <h3 className="title">
              <CustomLink href={buyHref} target="_blank">
                {title}
              </CustomLink>
            </h3>
            {description && <p className="description">{description}</p>}
            <br />
          </ShopCardSpecSection>
          <ShopCardFooterRow
            aria-hidden={isOpen}
            style={isOpen ? { visibility: 'hidden' } : undefined}
          >
              <ShopCardPriceCluster className={`pricing-info ${styles.pricingInfo}`}>
                {hasMixedPrices && !selectedColor && (
                  <span style={{ flexBasis: '100%' }}>
                    {tCart('sharedCart.upsell.startsFrom')}
                  </span>
                )}
                {originalPrice && discountPercentage && (
                  <span className="striked">
                    {CurrencySymbol[currency]}
                    {formatPriceWithComma(parseFloat(originalPrice))}
                  </span>
                )}
                {CurrencySymbol[currency]}
                {formatPriceWithComma(
                  originalPrice && discountPercentage
                    ? displayPrice * (1 - parseFloat(discountPercentage) / 100)
                    : displayPrice,
                )}
                <span>{CartClient.taxString} </span>
              </ShopCardPriceCluster>
              <ShopCardPrimaryCta
                className={isLoading ? 'quickAdd disable' : 'quickAdd'}
                data-product={title}
                data-buttontype="show color"
                onClick={(e) => {
                  e.stopPropagation();
                  setStep('color');
                }}
                disabled={isLoading}
              >
                {tCart('sharedCart.upsell.addToCart')}
              </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>
        {tag ? <ShopCardTag $heightMode="ring"><ShopCardTagText tag={tag} /></ShopCardTag> : null}

        <ShopCardMediaDimBackdrop className={isOpen ? 'open' : ''} />

        <ShopCardBottomSheet
          className={`sizeOverlay ${isOpen ? 'open' : ''} ${styles.bottomSheet}`}
          ref={overlayRef}
          aria-hidden={!isOpen}
        >
          <div className="stepContent" key={step === 'closed' ? 'color' : step}>
            {(step === 'color' || step === 'closed') && (
              <>
                <div className="stepHeader">
                  <div className="heading">{t('packs.color')}</div>
                </div>
                <div className="colorSwatches">
                  {colors.map((c) => (
                    <button
                      key={c.key}
                      className={`color-swatch-btn ${
                        selectedColorKey === c.key ? 'active' : ''
                      }`}
                      data-product={title}
                      data-buttontype="select color"
                      onClick={() => setSelectedColorKey(c.key)}
                      aria-label={c.name}
                      aria-pressed={selectedColorKey === c.key}
                    >
                      <Image
                        src={c.swatch}
                        alt={c.name}
                        width={48}
                        height={48}
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    </button>
                  ))}
                </div>
                {selectedColor && (
                  <div className="selectedColorName">{selectedColor.name}</div>
                )}
                <button
                  className="addToCart"
                  data-product={title}
                  data-buttontype="go to size step"
                  disabled={!selectedColor || isLoading}
                  onClick={() => setStep('size')}
                >
                  {t('packs.selectSize')}
                </button>
              </>
            )}
            {step === 'size' && selectedColor && (
              <>
                <div className="stepHeader">
                  <button
                    className="overlayBack"
                    onClick={() => setStep('color')}
                    data-buttontype="back to color"
                    aria-label={t('packs.back')}
                  >
                    ←
                  </button>
                  <div className="heading">{tCart('sharedCart.upsell.size')} </div>
                </div>
                <div className="sizes">
                  {selectedColor.sizes
                    .filter((s) => !s.isOpenSize)
                    .map((s) => (
                      <button
                        key={s.size}
                        className={
                          selectedSize === s.size ? 'selectedRingSize' : ''
                        }
                        data-product={title}
                        data-buttontype="select size"
                        onClick={() => setSelectedSize(s.size)}
                        aria-pressed={selectedSize === s.size}
                      >
                        {s.size}
                      </button>
                    ))}
                </div>
                {sizingKit && hasOpenSize && (
                  <div className="sizingKit">
                    <h4>{t('packs.sizingKitPrompt')}</h4>
                    <button
                      data-product={title}
                      data-buttontype="select sizing kit"
                      className={
                        selectedSize === RingSizes.OPEN
                          ? 'selectedRingSize'
                          : ''
                      }
                      onClick={() => setSelectedSize(RingSizes.OPEN)}
                      aria-pressed={selectedSize === RingSizes.OPEN}
                    >
                      <Image
                        src="web_v2/ring-buy/sizing-kit-1.png"
                        width={50}
                        height={50}
                        alt="sizing_kit"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className="content">
                        <div className="title">{t('packs.sizingKitTitle')}</div>
                        <div className="subtitle">
                          {t('packs.sizingKitSubtitle')}
                        </div>
                      </div>
                    </button>
                  </div>
                )}
                <button
                  className="addToCart"
                  data-product={title}
                  data-buttontype="add to cart"
                  data-variantId={
                    selectedSize != null
                      ? selectedColor.sizes.find((s) => s.size === selectedSize)
                          ?.shopifyVariantId
                      : ''
                  }
                  disabled={selectedSize == null || isLoading}
                  onClick={handleAddToCart}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <DotSpinner color="#FFFFFF" />
                  ) : (
                    <>
                      {tCart('sharedCart.upsell.addToCartFor', {
                        price: `${getCurrencySymbolFromEnum(currency)}${formatPriceWithComma(selectedColor.price)}`,
                      })}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </ShopCardBottomSheet>
      </ShopProductCardRoot>
    );
};
